// Prototype periodization palette (base → race). Kept in sync with `phaseBucket`.

/**
 * Group any plan's phase names (marathon + half differ) into a canonical
 * periodization bucket + bar color. Phases: Base, Build, "Peak / Specific",
 * Taper, "Race Week". Unknown phases fall back to the taper bucket.
 */
export function phaseBucket(phase: string): { label: string; color: string } {
  const p = phase.toLowerCase();
  if (p.startsWith("base")) return { label: "Base", color: "#E4EFA1" };
  if (p.startsWith("build")) return { label: "Build", color: "#C5DC8A" };
  if (p.startsWith("peak")) return { label: "Peak", color: "#88B655" };
  if (p.startsWith("race")) return { label: "Race", color: "#FFD176" };
  return { label: "Taper", color: "#D8C7F2" }; // taper (+ any unknown)
}

export function phaseColor(phase: string): string {
  return phaseBucket(phase).color;
}

export const PHASE_LEGEND = [
  { label: "Base", bar: "#E4EFA1" },
  { label: "Build", bar: "#C5DC8A" },
  { label: "Peak", bar: "#88B655" },
  { label: "Taper", bar: "#D8C7F2" },
  { label: "Race", bar: "#FFD176" }
] as const;

/**
 * Aggregate a plan's weeks into ordered phase segments for the mini phase bar
 * on the Plan-length step (prototype step 3). Phases run sequentially, so
 * bucketing by canonical label and ordering Base→Race reads left-to-right.
 */
export const PHASE_ORDER = ["Base", "Build", "Peak", "Taper", "Race"] as const;
export function phaseSegments(
  weekly: { phase: string }[]
): { label: string; color: string; weeks: number }[] {
  const buckets = new Map<string, { color: string; weeks: number }>();
  for (const w of weekly) {
    const b = phaseBucket(w.phase);
    const cur = buckets.get(b.label);
    if (cur) cur.weeks += 1;
    else buckets.set(b.label, { color: b.color, weeks: 1 });
  }
  return PHASE_ORDER.filter((l) => buckets.has(l)).map((l) => ({ label: l, ...buckets.get(l)! }));
}
